'use client';

import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { fadeIn } from '@/lib/animations';
import { ctfImage } from '@/lib/contentful-url';

export interface ImageOverlay {
  content: ReactNode;
}

export interface TextImageSectionProps {
  subtitle?: string;
  title: string;
  content: ReactNode;
  imageUrl: string;
  imageAlt: string;
  buttonText?: string;
  buttonHref?: string;
  reverse?: boolean;
  backgroundColor?: string;
  sectionClassName?: string;
  imageOverlay?: ImageOverlay;
}

export function TextImageSection({
  subtitle,
  title,
  content,
  imageUrl,
  imageAlt,
  buttonText,
  buttonHref = '#contact',
  reverse = false,
  backgroundColor = 'bg-white',
  sectionClassName = '',
  imageOverlay,
}: TextImageSectionProps) {
  const imageSide = (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      variants={fadeIn(reverse ? 'left' : 'right')}
      className="relative"
    >
      <div className="absolute -top-6 -left-6 w-32 h-32 bg-brand-600 rounded-tl-2xl"></div>
      <img
        src={ctfImage(imageUrl, { width: 800, height: 1000, fit: 'fill', quality: 60 })}
        srcSet={[400, 600, 800, 1200]
          .map(
            (w) =>
              `${ctfImage(imageUrl, { width: w, height: w * 1.25, fit: 'fill', quality: 60 })} ${w}w`
          )
          .join(', ')}
        sizes="(min-width: 1024px) 45vw, 100vw"
        alt={imageAlt}
        loading="lazy"
        decoding="async"
        className="rounded-lg shadow-xl relative z-10 w-full h-[500px] object-cover"
      />
      <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-brand-100 rounded-br-2xl"></div>
      {imageOverlay && (
        <div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-sm rounded-lg p-6 z-20">
          {imageOverlay.content}
        </div>
      )}
    </motion.div>
  );

  const textSide = (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      variants={fadeIn(reverse ? 'right' : 'left')}
    >
      {subtitle && (
        <h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-2">
          {subtitle}
        </h2>
      )}
      {title && (
        <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
          {title}
        </h3>
      )}
      
      <div className="text-lg text-gray-700 leading-relaxed mb-8">
        {content}
      </div>
      
      {buttonText && (
        <Button asChild className="bg-brand-600 hover:bg-brand-700 text-white px-8 py-6 text-lg">
          <a href={buttonHref}>
            {buttonText}
            {buttonText.includes('Contact') && <ArrowRight className="ml-2" size={20} />}
          </a>
        </Button>
      )}
    </motion.div>
  );

  return (
    <section className={`py-8 md:py-12 ${backgroundColor} ${sectionClassName}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {reverse ? (
            <>
              {imageSide}
              {textSide}
            </>
          ) : (
            <>
              {textSide}
              {imageSide}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

